import React from "react";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import { Truck, Clock, MapPin, ArrowUpRight } from "lucide-react";
import { ACTIVE_LOCATIONS, DC_AREAS } from "../lib/constants";

export default function Delivery() {
  return (
    <>
      <SEO
        title="Same-Day Weed Delivery in Washington, DC | DWC"
        description="Fast, same-day medical cannabis delivery from 4915 Georgia Ave NW (20011) — Petworth, Brightwood, 16th Street Heights, Park View, Columbia Heights, Shaw, Dupont, Georgetown, Capitol Hill, Navy Yard & every DC neighborhood."
        path="/delivery"
        breadcrumbs={[{ name: "Delivery", path: "/delivery" }]}
        service={{
          name: "Same-Day Cannabis Delivery in Washington, DC",
          description: "Licensed same-day medical cannabis delivery across Washington DC from our dispensary at 4915 Georgia Ave NW, 20011 — flower, edibles, vapes, concentrates and CBD.",
          areaServed: [
            "Washington DC 20011","Petworth","Brightwood","16th Street Heights","Park View","Crestwood","Manor Park","Takoma DC","Columbia Heights","Shaw","Adams Morgan","Dupont Circle","Georgetown","Capitol Hill","Navy Yard","Brookland","Tenleytown","Chevy Chase DC","Fort Totten","Palisades",
          ],
        }}
      />

      <section id="delivery" className="relative bg-white overflow-hidden" data-testid="delivery-page">
        <div className="max-w-7xl mx-auto px-6 py-20 sm:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">Same-Day · Licensed · Discreet</div>
            <h1 className="font-serif text-5xl sm:text-7xl leading-[0.95] tracking-tight">
              Weed delivery<br/>across <em className="text-brand">Washington, DC</em>.
            </h1>
            <p className="mt-6 text-ink-muted text-lg max-w-xl leading-relaxed">
              Order from our Georgia Ave dispensary and we'll bring premium medical cannabis right to your door —
              typically within the hour. Flower, vapes, edibles, concentrates and more.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/menu" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em]" data-testid="delivery-order-btn">
                Start Your Order <ArrowUpRight size={14} />
              </Link>
              <Link to="/areas-served" className="inline-flex items-center gap-2 bg-ink text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em]" data-testid="delivery-areas-btn">
                See Areas Served
              </Link>
            </div>

            <div className="mt-14 grid sm:grid-cols-3 gap-0 border border-black/10">
              {[
                { icon: Truck, t: "Same-Day", s: "Order by 7pm for today" },
                { icon: Clock, t: "~60 min", s: "Avg delivery window" },
                { icon: MapPin, t: "21+ DC", s: "Licensed patients only" },
              ].map((x, i) => {
                const Icon = x.icon;
                return (
                  <div key={i} className={`p-6 ${i !== 2 ? "border-r border-black/10" : ""}`}>
                    <Icon size={18} className="text-brand mb-3" />
                    <div className="font-serif text-2xl leading-none">{x.t}</div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted mt-2">{x.s}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="relative">
            <div className="relative overflow-hidden border border-black/10 bg-[#FAFAFA]">
              <img src="https://images.pexels.com/photos/19764055/pexels-photo-19764055.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="DC street" className="w-full h-[500px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-ink text-white px-6 py-5 max-w-xs">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Delivery window</div>
              <div className="font-serif text-2xl leading-tight">Open 7 days · Mon–Thu 8am–10pm</div>
              <div className="text-sm mt-1">Fri–Sat 8am–11pm · Sun 9am–9pm</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-4">Delivering From</div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1] tracking-tight">{ACTIVE_LOCATIONS.length === 1 ? "Our Georgia Ave flagship." : "Our hubs."}</h2>
          </div>
          <div className={`grid gap-5 ${ACTIVE_LOCATIONS.length === 1 ? "md:grid-cols-1 max-w-2xl" : "md:grid-cols-3"}`}>
            {ACTIVE_LOCATIONS.map((loc) => (
              <div key={loc.id} className="p-8 bg-white border border-black/10" data-testid={`delivery-hub-${loc.id}`}>
                <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-3">{loc.label}</div>
                <div className="font-serif text-2xl">{loc.name}</div>
                <div className="text-base text-ink-muted mt-2">{loc.street} · {loc.city}</div>
              </div>
            ))}
          </div>

          <div className="mt-16">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-4">Popular delivery zones</div>
            <div className="flex flex-wrap gap-2">
              {DC_AREAS.slice(0, 12).map((a) => (
                <Link key={a.slug} to={`/areas-served#${a.slug}`} className="px-4 py-2 border border-black/15 hover:border-brand hover:text-brand text-sm">
                  {a.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
